"use client";

import { useMemo } from "react";
import Link from "next/link";
import { ChevronLeft as ChevronLeftIcon } from "lucide-react";
import CdnImage from "@/components/CdnImage";
import EventMultiOutcomeChart from "@/app/components/EventMultiOutcomeChart";
import type { EventData } from "@/hooks/useEventQuery";
import { useKalshiLiveTickers } from "@/hooks/useKalshiLiveTickers";
import { useKalshiLiveState } from "@/hooks/useKalshiLiveState";
import { useKalshiLiveData } from "@/hooks/useKalshiLiveData";
import { useKalshiRelatedMarkets, type RelatedGroup, type RelatedMarket } from "@/hooks/useKalshiRelatedMarkets";
import { useTradingModalStore } from "@/stores/useTradingModalStore";
import KalshiMatchInfo from "./KalshiMatchInfo";

interface KalshiSportsHeroProps {
  eventData: EventData;
  slug: string;
  formatVolume: (n: number | null | undefined) => string;
  competition?: string;
  startDate?: string | null;
}

function getKalshiTicker(eventData: EventData, slug: string): string {
  return ((eventData as any)?.eventTicker || (eventData as any)?.id || slug || "").toUpperCase();
}

function formatStartDate(iso?: string | null): string | null {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return null;
  }
}

function formatClock(seconds?: number | null): string | null {
  if (seconds == null || !Number.isFinite(seconds)) return null;
  const total = Math.max(0, Math.floor(seconds));
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${mins}:${String(secs).padStart(2, "0")}`;
}

function parseMatchup(title?: string | null): [string, string] | null {
  if (!title) return null;
  const m = title.match(/^(.+?)\s+(?:vs\.?|@|at|–|-)\s+(.+)$/i);
  if (!m) return null;
  return [m[1].trim(), m[2].trim()];
}

function normalizeName(value?: string | null): string {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

/**
 * Polymarket-style sports event hero adapted for Kalshi.
 * - Top bar: back chevron + series image + title + Vol/OI/Markets pills + Kalshi link
 * - Matchup: large team logos + names + center score / VS
 * - Live row with red badge + period + price split bar
 * - Tournament + start time chips
 * - Multi-outcome chart below
 */
export default function KalshiSportsHero({
  eventData,
  slug,
  formatVolume,
  competition,
  startDate,
}: KalshiSportsHeroProps) {
  const eventTicker = getKalshiTicker(eventData, slug);
  const { data: liveSet } = useKalshiLiveTickers(true);
  const { data: liveState } = useKalshiLiveState(true);
  const { data: related } = useKalshiRelatedMarkets(eventTicker);
  const isLive = !!liveSet?.has(eventTicker);
  // Primary snapshot — the Redis-cached map built by the worker.
  const bulkSnap = liveState?.[eventTicker];
  // Always fetch per-event — the bulk snapshot doesn't carry the rich
  // match-info stats (tennis competitor_statistics, team stats…) that the
  // Match info dropdown needs. Also covers: esports map rows, recently
  // finished matches (worker drops them) and pre-match events.
  const needsFallback = true;
  const { data: perEventLive } = useKalshiLiveData(
    needsFallback ? eventTicker : null,
    needsFallback
  );
  const perEventSnap = perEventLive
    ? {
        type: perEventLive.type,
        status: perEventLive.status,
        widgetStatus: perEventLive.widgetStatus,
        statusText: (perEventLive as any).statusText,
        homeScore: perEventLive.homePoints,
        awayScore: perEventLive.awayPoints,
        homeWickets: (perEventLive as any).homeWickets,
        awayWickets: (perEventLive as any).awayWickets,
        homeOvers: (perEventLive as any).homeOvers,
        awayOvers: (perEventLive as any).awayOvers,
        batting: (perEventLive as any).batting,
        lastPlay: (perEventLive as any).lastPlay,
        period: perEventLive.period,
        periodLabel: perEventLive.periodLabel,
        clock: perEventLive.clock,
        winner: perEventLive.winner,
        competitors: perEventLive.competitors,
        raw: perEventLive.raw,
      }
    : undefined;
  // Merge per-event on top of bulk, but only pick defined keys so we don't
  // blow away a good bulk score with undefined from the per-event path (e.g.
  // soccer multi-leg when the per-event normalizer misses a fallback field).
  const liveSnap: any = (() => {
    if (!bulkSnap && !perEventSnap) return undefined;
    if (!perEventSnap) return bulkSnap;
    if (!bulkSnap) return perEventSnap;
    const merged: any = { ...bulkSnap };
    for (const [k, v] of Object.entries(perEventSnap)) {
      if (v !== undefined) merged[k] = v;
    }
    return merged;
  })();
  const isEsportsMatch = liveSnap?.type === "esports_match" || bulkSnap?.type === "esports_match";
  const hasRichEsportsDetails = !!((liveSnap as any)?.raw?.details || (perEventSnap as any)?.raw?.details);
  const holdEsportsInterimState = !!(isEsportsMatch && !hasRichEsportsDetails);

  const allMarkets = (eventData?.markets || []) as any[];
  const matchup = parseMatchup((eventData as any)?.title);
  const findByTitlePart = (part?: string | null) => {
    const needle = normalizeName(part);
    if (!needle) return undefined;
    return allMarkets.find((m: any) => {
      const titleNorm = normalizeName(m?.title || m?.question);
      return titleNorm === needle || titleNorm.includes(needle) || needle.includes(titleNorm);
    });
  };
  let homeMarket = findByTitlePart(matchup?.[0]) || allMarkets[0];
  let awayMarket = findByTitlePart(matchup?.[1]) || allMarkets.find((m: any) => m !== homeMarket) || allMarkets[0];
  const markets = allMarkets;

  const homeFullName = homeMarket?.title || homeMarket?.question || "Home";
  const awayFullName = awayMarket?.title || awayMarket?.question || "Away";
  // Prefer the shorter team label under each logo — stops long names like
  // "Royal Challengers Bengaluru" from getting truncated at 160px.
  const homeName = homeMarket?.shortName || homeFullName;
  const awayName = awayMarket?.shortName || awayFullName;
  // Kalshi-style short codes ("RCB", "GT") when the search-series payload
  // gave us an abbreviation/shortName. Falls back to the first-3-chars slug.
  const abbrFromMarket = (m: any, fallback: string) => {
    const a = m?.abbreviation || m?.shortName;
    if (typeof a === 'string' && a.trim()) return a.trim().toUpperCase();
    return fallback.slice(0, 3).toUpperCase();
  };
  const homeAbbr = abbrFromMarket(homeMarket, homeFullName);
  const awayAbbr = abbrFromMarket(awayMarket, awayFullName);
  const homeImg = (homeMarket as any)?.image || (homeMarket as any)?.imageUrl || null;
  const awayImg = (awayMarket as any)?.image || (awayMarket as any)?.imageUrl || null;

  const homePriceRaw =
    typeof homeMarket?.lastPrice === "number"
      ? homeMarket.lastPrice
      : Array.isArray(homeMarket?.outcomePrices)
      ? Number(homeMarket.outcomePrices[0])
      : 0;
  const awayPriceRaw =
    typeof awayMarket?.lastPrice === "number"
      ? awayMarket.lastPrice
      : Array.isArray(awayMarket?.outcomePrices)
      ? Number(awayMarket.outcomePrices[0])
      : 0;
  const homePct = Math.round((homePriceRaw || 0) * 100);
  const awayPct = Math.round((awayPriceRaw || 0) * 100);

  // Match each row to live competitor by structured_target id when present,
  // otherwise fall back to the markets[] order (most Kalshi events return
  // markets in the same order as competitor1/competitor2).
  const competitors: any[] = liveSnap?.competitors || [];
  const findCompetitor = (m: any, idx: number) => {
    if (m?.structuredTargetId) {
      const byId = competitors.find((c: any) => c.id === m.structuredTargetId);
      if (byId) return byId;
    }
    return competitors[idx];
  };
  const homeC = findCompetitor(homeMarket, 0);
  const awayC = findCompetitor(awayMarket, 1);

  const scoreForMarket = (market: any, competitor: any, fallbackScore?: number) => {
    if (market?.teamId && liveSnap?.homeTeamId && market.teamId === liveSnap.homeTeamId) return liveSnap?.homeScore;
    if (market?.structuredTargetId && liveSnap?.homeTeamId && market.structuredTargetId === liveSnap.homeTeamId) return liveSnap?.homeScore;
    if (market?.teamId && liveSnap?.awayTeamId && market.teamId === liveSnap.awayTeamId) return liveSnap?.awayScore;
    if (market?.structuredTargetId && liveSnap?.awayTeamId && market.structuredTargetId === liveSnap.awayTeamId) return liveSnap?.awayScore;
    return competitor?.overallScore ?? fallbackScore;
  };
  const homeScore = scoreForMarket(homeMarket, homeC, liveSnap?.homeScore);
  const awayScore = scoreForMarket(awayMarket, awayC, liveSnap?.awayScore);
  const homeSets = homeC?.roundScores;
  const awaySets = awayC?.roundScores;
  const homeCurrent = homeC?.currentRoundScore;
  const awayCurrent = awayC?.currentRoundScore;
  const rawHomePeriods = (liveSnap as any)?.raw?.details?.home_periods || {};
  const rawAwayPeriods = (liveSnap as any)?.raw?.details?.away_periods || {};
  const rawHomeStats = (liveSnap as any)?.raw?.details?.home_stats || [];
  const rawAwayStats = (liveSnap as any)?.raw?.details?.away_stats || [];
  const rawFormat = String((liveSnap as any)?.raw?.details?.format || "");
  const formatMatch = rawFormat.match(/^BO(\d+)$/i);
  const periodKeys = Array.from(
    new Set([
      ...Object.keys(rawHomePeriods),
      ...Object.keys(rawAwayPeriods),
    ]),
  )
    .map((key) => {
      const match = key.match(/^period_(\d+)$/i);
      return match ? Number(match[1]) : null;
    })
    .filter((value): value is number => value != null)
    .sort((a, b) => a - b);
  const esportsMatch = typeof liveSnap?.periodLabel === "string"
    ? liveSnap.periodLabel.match(/^(\d+)\/(\d+)$/)
    : null;
  const inferredCurrentMap = periodKeys.length > 0 ? Math.max(...periodKeys) : null;
  const inferredTotalMaps = formatMatch ? Number(formatMatch[1]) : periodKeys.length || null;
  const isEsportsSeries = !!(
    liveSnap?.type === "esports_match" &&
    ((inferredCurrentMap != null && inferredTotalMaps != null) || esportsMatch)
  );
  const currentMapIndex = esportsMatch ? Number(esportsMatch[1]) : inferredCurrentMap;
  const totalMaps = esportsMatch ? Number(esportsMatch[2]) : inferredTotalMaps;
  const currentMapHomeScore =
    currentMapIndex != null ? rawHomePeriods[`period_${currentMapIndex}`] ?? null : null;
  const currentMapAwayScore =
    currentMapIndex != null ? rawAwayPeriods[`period_${currentMapIndex}`] ?? null : null;
  const esportsMapRows = useMemo(() => {
    if (!isEsportsSeries || !totalMaps) return [];
    const rows: Array<{ map: number; clock: string | null; home: number | null; away: number | null }> = [];
    for (let i = 1; i <= totalMaps; i += 1) {
      const periodKey = `period_${i}`;
      const home = rawHomePeriods[periodKey] ?? null;
      const away = rawAwayPeriods[periodKey] ?? null;
      const homeStats = rawHomeStats.find((s: any) => s?.period === periodKey);
      const awayStats = rawAwayStats.find((s: any) => s?.period === periodKey);
      const duration =
        homeStats?.stats?.map_duration_seconds ??
        awayStats?.stats?.map_duration_seconds ??
        (i === currentMapIndex ? (liveSnap as any)?.raw?.details?.current_game_duration_seconds : null);
      rows.push({
        map: i,
        clock: formatClock(duration),
        home,
        away,
      });
    }
    return rows;
  }, [currentMapIndex, isEsportsSeries, liveSnap, rawAwayPeriods, rawAwayStats, rawHomePeriods, rawHomeStats, totalMaps]);
  const mainHomeScore = isEsportsSeries ? homeScore : homeScore;
  const mainAwayScore = isEsportsSeries ? awayScore : awayScore;

  // Cricket: Kalshi renders "176/5 - 205/3" with overs under each side.
  const isCricket = liveSnap?.type === 'cricket_match';
  const homeWickets = (liveSnap as any)?.homeWickets ?? null;
  const awayWickets = (liveSnap as any)?.awayWickets ?? null;
  const homeOvers = (liveSnap as any)?.homeOvers ?? null;
  const awayOvers = (liveSnap as any)?.awayOvers ?? null;
  const lastPlay: string | undefined = (liveSnap as any)?.lastPlay;

  const isFinished =
    liveSnap?.widgetStatus === "finished" ||
    liveSnap?.status === "closed" ||
    // Kalshi post-match: cricket sets status "ended" + widget_status "none"
    // + a non-empty winner. Without this, the hero falls back to the "VS"
    // placeholder even though we have a final score to show.
    liveSnap?.status === "ended" ||
    !!liveSnap?.winner ||
    !!eventData?.closed;
  const heroWinner: "home" | "away" | null = (() => {
    if (!isFinished) return null;
    if (typeof homeScore === "number" && typeof awayScore === "number") {
      if (homeScore > awayScore) return "home";
      if (awayScore > homeScore) return "away";
    }
    // Fallback for finished events without live snap: derive from yesPrice
    if (homePriceRaw >= 0.99 || (homePriceRaw >= 0.5 && awayPriceRaw <= 0.01)) return "home";
    if (awayPriceRaw >= 0.99 || (awayPriceRaw >= 0.5 && homePriceRaw <= 0.01)) return "away";
    return null;
  })();

  const startTimeDisplay = formatStartDate(startDate);
  const tournament = competition || (eventData as any)?.competition;

  const seriesImage = (eventData as any)?.image;

  // Build chart-friendly markets array
  const chartMarkets = markets.filter((m) => m && (m.title || m.question));

  return (
    <div className="bg-white dark:bg-[#141418] rounded-[5px] border border-gray-100 dark:border-[#1e1e24] overflow-hidden">
      {/* Top bar */}
      <div className="px-4 md:px-6 pt-3 pb-2">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <Link
              href="/kalshi/markets/sports"
              className="inline-flex items-center justify-center w-7 h-7 rounded-[5px] text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors flex-shrink-0"
            >
              <ChevronLeftIcon size={18} />
            </Link>
            {seriesImage && (
              <div className="relative flex-shrink-0 w-8 h-8 md:w-[50px] md:h-[50px] rounded-[5px] overflow-hidden bg-gray-100 dark:bg-[#0f0f12] border border-gray-200 dark:border-[#1e1e24]">
                <CdnImage src={seriesImage} alt="" fill className="object-cover" />
              </div>
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-inter-tight text-[15px] md:text-[17px] font-semibold text-gray-900 dark:text-white truncate">
                  {homeFullName} vs {awayFullName}
                </h1>
              </div>
              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                <span className="px-2 py-0.5 rounded-[5px] bg-gray-100 dark:bg-[#1e1e24] text-[10px] font-medium text-gray-500 dark:text-gray-400">
                  Vol {formatVolume(eventData.volume)}
                </span>
                {eventData.openInterest != null && eventData.openInterest > 0 && (
                  <span className="px-2 py-0.5 rounded-[5px] bg-gray-100 dark:bg-[#1e1e24] text-[10px] font-medium text-gray-500 dark:text-gray-400">
                    OI {formatVolume(eventData.openInterest)}
                  </span>
                )}
                <span className="px-2 py-0.5 rounded-[5px] bg-gray-100 dark:bg-[#1e1e24] text-[10px] font-medium text-gray-500 dark:text-gray-400">
                  {markets.length} Markets
                </span>
              </div>
            </div>
          </div>
          <a
            href={`https://kalshi.com/markets/${(eventData as any)?.seriesTicker?.toLowerCase() || ""}/${slug.toLowerCase()}`}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="flex-shrink-0 mt-0.5 opacity-60 hover:opacity-100 transition-opacity"
            title="View on Kalshi"
          >
            <span className="md:hidden w-5 h-5 rounded-[5px] flex items-center justify-center bg-[#00D26A]">
              <img src="/logos/kalshi/k-icon.svg" alt="Kalshi" className="w-3.5 h-3.5 object-contain" />
            </span>
            <span className="hidden md:inline-flex">
              <img src="/logos/kalshi/kalshi-logo.svg" alt="Kalshi" className="h-3.5" />
            </span>
          </a>
        </div>
      </div>

      {/* Matchup */}
      <div className="relative px-6 md:px-10 lg:px-16 pt-4 pb-5 overflow-hidden">
        {seriesImage && (
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage: `url(${seriesImage})`,
              backgroundSize: "cover",
              backgroundPosition: "top",
              maskImage: "linear-gradient(to bottom, rgba(0,0,0,0.12) 0%, rgba(0,0,0,0.06) 50%, transparent 100%)",
              WebkitMaskImage: "linear-gradient(to bottom, rgba(0,0,0,0.12) 0%, rgba(0,0,0,0.06) 50%, transparent 100%)",
            }}
          />
        )}
        <div className="flex flex-col items-center max-w-3xl mx-auto gap-3 relative">
          <div className="flex items-center justify-center w-full gap-3 md:gap-6">
            {/* Home */}
            <div className={`flex flex-col items-center gap-1 flex-1 min-w-0 ${heroWinner && heroWinner !== "home" ? "opacity-40" : ""}`}>
              <div className="rounded-[5px] flex-shrink-0 p-0.5">
                {homeImg ? (
                  <CdnImage
                    src={homeImg}
                    alt=""
                    width={96}
                    height={96}
                    className="w-10 h-10 md:w-14 md:h-14 lg:w-16 lg:h-16 rounded-[5px] object-contain"
                  />
                ) : (
                  <div className="w-10 h-10 md:w-14 md:h-14 lg:w-16 lg:h-16 rounded-[5px] bg-gray-100 dark:bg-[#1e1e24] flex items-center justify-center text-lg md:text-xl font-bold text-gray-400">
                    {homeName.charAt(0)}
                  </div>
                )}
              </div>
              <span className="font-monument text-[10px] md:text-[12px] text-gray-900 dark:text-white truncate block max-w-[120px] md:max-w-[160px] text-center">
                {homeName}
              </span>
            </div>

            {/* Center — items-center on the row now vertically aligns logos
                and score because the price bar + tournament chips have been
                hoisted out of this column (rendered below the row instead). */}
            <div className="text-center flex-shrink-0">
              {isFinished && homeScore == null && awayScore == null && (!homeSets || homeSets.length === 0) ? (
                <span className="inline-block px-3 py-1 bg-red-100 dark:bg-red-500/15 text-red-600 dark:text-red-400 text-[12px] font-bold rounded">
                  FINAL
                </span>
              ) : isLive || isFinished ? (
                <>
                  {holdEsportsInterimState ? (
                    <div className="flex items-center justify-center min-w-[240px] h-[78px]">
                      <span className="px-2 py-0.5 rounded-[5px] text-[10px] font-bold bg-gray-100 dark:bg-[#1e1e24] text-gray-500 dark:text-gray-400">
                        Loading live map data
                      </span>
                    </div>
                  ) : isEsportsSeries && esportsMapRows.length > 0 ? (
                    <div className="flex items-stretch justify-center gap-2 min-w-[240px]">
                      {esportsMapRows.map((row) => {
                        const isCurrentMap = row.map === currentMapIndex;
                        return (
                          <div
                            key={row.map}
                            className={`rounded-[5px] px-2.5 py-2 flex flex-col items-center ${
                              isCurrentMap
                                ? "bg-red-500/8 border border-red-500/15"
                                : "bg-gray-50 dark:bg-white/[0.04] border border-transparent"
                            }`}
                          >
                            <div className="font-monument text-[10px] text-gray-700 dark:text-gray-200 leading-none">
                              {row.clock || `M${row.map}`}
                            </div>
                            <div className="mt-2 flex items-center gap-1.5 font-monument text-[16px] md:text-[18px] text-gray-900 dark:text-white tabular-nums leading-none">
                              <span>{row.home ?? '-'}</span>
                              <span className="text-gray-400 dark:text-gray-500">-</span>
                              <span>{row.away ?? '-'}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : homeSets && awaySets && homeSets.length > 0 ? (
                    // Tennis: horizontal per-set layout — each column is one
                    // set reading top=home / bottom=away (matching the side
                    // logos), with "Set N" label underneath. The set winner's
                    // score is bold, the loser's dim.
                    (() => {
                      const cellClass = (mine: number, theirs: number) =>
                        mine > theirs
                          ? 'min-w-[34px] md:min-w-[42px] px-2.5 py-1.5 rounded bg-gray-100 dark:bg-[#0f0f12] text-center text-[17px] md:text-[22px] font-black text-gray-900 dark:text-white'
                          : 'min-w-[34px] md:min-w-[42px] px-2.5 py-1.5 rounded bg-gray-50 dark:bg-[#18181D] text-center text-[17px] md:text-[22px] font-medium text-gray-400 dark:text-gray-500';
                      const liveCellClass =
                        'min-w-[34px] md:min-w-[42px] px-2.5 py-1.5 rounded bg-emerald-500/15 text-center text-[17px] md:text-[22px] font-bold text-emerald-500';
                      const hasHomeCurrent =
                        !isFinished && homeCurrent != null && homeCurrent !== 0 && homeCurrent !== '0';
                      const hasAwayCurrent =
                        !isFinished && awayCurrent != null && awayCurrent !== 0 && awayCurrent !== '0';
                      const totalCols =
                        Math.max(homeSets.length, awaySets.length) +
                        (hasHomeCurrent || hasAwayCurrent ? 1 : 0);
                      const liveSetIndex = homeSets.length; // the in-progress set
                      return (
                        <div className="flex items-end justify-center gap-5 md:gap-7 tabular-nums">
                          {Array.from({ length: totalCols }).map((_, i) => {
                            const isLiveSet =
                              (hasHomeCurrent || hasAwayCurrent) && i === liveSetIndex;
                            const homeScoreCell = isLiveSet
                              ? (hasHomeCurrent ? (
                                  <span className={liveCellClass}>{String(homeCurrent)}</span>
                                ) : (
                                  <span className={cellClass(0, 1)}>-</span>
                                ))
                              : (
                                  <span className={cellClass(homeSets[i] ?? 0, awaySets[i] ?? 0)}>
                                    {homeSets[i] ?? '-'}
                                  </span>
                                );
                            const awayScoreCell = isLiveSet
                              ? (hasAwayCurrent ? (
                                  <span className={liveCellClass}>{String(awayCurrent)}</span>
                                ) : (
                                  <span className={cellClass(0, 1)}>-</span>
                                ))
                              : (
                                  <span className={cellClass(awaySets[i] ?? 0, homeSets[i] ?? 0)}>
                                    {awaySets[i] ?? '-'}
                                  </span>
                                );
                            return (
                              <div key={`set${i}`} className="flex flex-col items-center gap-1">
                                <div className="flex items-center gap-1">
                                  {homeScoreCell}
                                  {awayScoreCell}
                                </div>
                                <span className="font-monument text-[9px] md:text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 mt-0.5">
                                  Set {i + 1}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()
                  ) : isCricket ? (
                    // Cricket: "{runs}/{wickets}" + "{ABBR}({overs})" per side,
                    // with a dash separator — mirrors kalshi.com's header.
                    <div className="flex items-start justify-center gap-2 tabular-nums">
                      <div className="flex flex-col items-center">
                        <span className="min-w-[54px] px-2.5 py-1.5 rounded-[5px] bg-gray-100 dark:bg-[#0f0f12] text-center font-monument text-[18px] md:text-[22px] text-gray-900 dark:text-white">
                          {mainHomeScore ?? 0}{homeWickets != null ? `/${homeWickets}` : ''}
                        </span>
                        <span className="mt-1 text-[10px] font-semibold text-gray-500 dark:text-gray-400">
                          {homeAbbr}{homeOvers != null ? `(${homeOvers})` : ''}
                        </span>
                      </div>
                      <div className="h-[34px] md:h-[40px] flex items-center px-1" aria-hidden="true">
                        <span className="inline-block w-3 md:w-4 h-[2px] bg-gray-400 dark:bg-gray-500 rounded-full" />
                      </div>
                      <div className="flex flex-col items-center">
                        <span className="min-w-[54px] px-2.5 py-1.5 rounded-[5px] bg-gray-100 dark:bg-[#0f0f12] text-center font-monument text-[18px] md:text-[22px] text-gray-900 dark:text-white">
                          {mainAwayScore ?? 0}{awayWickets != null ? `/${awayWickets}` : ''}
                        </span>
                        <span className="mt-1 text-[10px] font-semibold text-gray-500 dark:text-gray-400">
                          {awayAbbr}{awayOvers != null ? `(${awayOvers})` : ''}
                        </span>
                      </div>
                    </div>
                  ) : mainHomeScore != null || mainAwayScore != null ? (
                    <div className="flex items-center justify-center gap-2 tabular-nums">
                      <span className="min-w-[36px] px-2.5 py-1.5 rounded-[5px] bg-gray-100 dark:bg-[#0f0f12] text-center font-monument text-[18px] md:text-[22px] text-gray-900 dark:text-white">
                        {mainHomeScore ?? 0}
                      </span>
                      <span className="self-center font-monument text-[16px] text-gray-400 dark:text-gray-500 px-0.5">-</span>
                      <span className="min-w-[36px] px-2.5 py-1.5 rounded-[5px] bg-gray-100 dark:bg-[#0f0f12] text-center font-monument text-[18px] md:text-[22px] text-gray-900 dark:text-white">
                        {mainAwayScore ?? 0}
                      </span>
                    </div>
                  ) : (
                    /* Finished without live data — just show "VS" so the
                       FINAL badge below tells the story */
                    <span className="font-monument text-2xl text-gray-400 dark:text-gray-500">VS</span>
                  )}
                  {isFinished && (
                    <span className="inline-block mt-2 px-2.5 py-0.5 bg-red-100 dark:bg-red-500/15 text-red-600 dark:text-red-400 text-[11px] font-bold rounded">FINAL</span>
                  )}
                  {isEsportsSeries && (
                    <div className="mt-2 flex items-center justify-center gap-2">
                      <span className="px-2 py-0.5 rounded-[5px] text-[10px] font-bold bg-gray-100 dark:bg-[#1e1e24] text-gray-600 dark:text-gray-300">
                        Series {homeScore ?? 0}–{awayScore ?? 0}
                      </span>
                      <span className="px-2 py-0.5 rounded-[5px] text-[10px] font-bold bg-red-500/10 text-red-500">
                        Map {currentMapIndex} / {totalMaps}
                        {currentMapHomeScore != null && currentMapAwayScore != null ? ` · ${currentMapHomeScore}-${currentMapAwayScore}` : ""}
                      </span>
                    </div>
                  )}
                  {(liveSnap?.periodLabel || isLive) && !isFinished && (
                    <div className="mt-2 flex flex-col items-center gap-1.5">
                      <div className="flex items-center justify-center gap-1.5">
                        {isLive && (
                          <span className="flex items-center gap-1 px-1.5 py-0.5 bg-red-500/10 text-red-500 text-[9px] font-bold rounded-[5px]">
                            <span className="w-1 h-1 bg-red-500 rounded-[5px] animate-pulse" />
                            LIVE
                          </span>
                        )}
                        {(liveSnap?.statusText || liveSnap?.periodLabel) && (
                          <span className="px-2 py-0.5 rounded-[5px] text-[10px] font-bold bg-gray-100 dark:bg-[#1e1e24] text-gray-600 dark:text-gray-300 inline-flex items-center gap-1">
                            {liveSnap.inningHalf === 'top' && <span className="text-emerald-500">▲</span>}
                            {liveSnap.inningHalf === 'bot' && <span className="text-emerald-500">▼</span>}
                            {liveSnap.statusText || liveSnap.periodLabel}
                          </span>
                        )}
                      </div>
                      {/* Baseball BSO indicator */}
                      {(liveSnap?.balls != null || liveSnap?.strikes != null || liveSnap?.outs != null) && (
                        <BSOIndicator
                          balls={liveSnap.balls ?? 0}
                          strikes={liveSnap.strikes ?? 0}
                          outs={liveSnap.outs ?? 0}
                        />
                      )}
                      {/* Cricket ball-by-ball commentary — Kalshi shows the last play
                          directly under the score widget. */}
                      {isCricket && lastPlay && (
                        <div className="max-w-[360px] text-center text-[11px] text-gray-500 dark:text-gray-400 italic leading-snug">
                          {lastPlay}
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (() => {
                // If kickoff time already passed but we still have no live
                // snapshot, the match is almost certainly about to start —
                // Kalshi labels this "Starting soon" (tennis/niche events).
                const ts = startDate ? Date.parse(startDate) : NaN;
                const startingSoon = Number.isFinite(ts) && ts - Date.now() <= 0;
                return startingSoon ? (
                  <span className="font-monument text-[14px] md:text-[16px] text-gray-500 dark:text-gray-400">
                    Starting soon
                  </span>
                ) : (
                  <span className="font-monument text-2xl text-gray-400 dark:text-gray-500">VS</span>
                );
              })()}
            </div>

            {/* Away */}
            <div className={`flex flex-col items-center gap-1 flex-1 min-w-0 ${heroWinner && heroWinner !== "away" ? "opacity-40" : ""}`}>
              <div className="rounded-[5px] flex-shrink-0 p-0.5">
                {awayImg ? (
                  <CdnImage
                    src={awayImg}
                    alt=""
                    width={96}
                    height={96}
                    className="w-10 h-10 md:w-14 md:h-14 lg:w-16 lg:h-16 rounded-[5px] object-contain"
                  />
                ) : (
                  <div className="w-10 h-10 md:w-14 md:h-14 lg:w-16 lg:h-16 rounded-[5px] bg-gray-100 dark:bg-[#1e1e24] flex items-center justify-center text-lg md:text-xl font-bold text-gray-400">
                    {awayName.charAt(0)}
                  </div>
                )}
              </div>
              <span className="font-monument text-[10px] md:text-[12px] text-gray-900 dark:text-white truncate block max-w-[120px] md:max-w-[160px] text-center">
                {awayName}
              </span>
            </div>
          </div>

          {/* Price split bar — hoisted out of the center column so the row
              above can cleanly vertically center the logos with the score. */}
          {(homePct > 0 || awayPct > 0) && (
            <div className="flex flex-col items-center gap-2">
              <div className="flex items-center gap-2 justify-center w-full max-w-[280px] md:max-w-none">
                <span className="font-monument text-[11px] md:text-[12px] text-emerald-600 dark:text-[#00FF9D] flex-shrink-0">{homePct}%</span>
                <div className="flex h-2 md:h-2.5 rounded-[5px] overflow-hidden flex-1 md:flex-none md:w-56 lg:w-64 gap-[1px]">
                  <div className="h-full rounded-l-full bg-emerald-500 dark:bg-[#00FF9D] transition-all duration-700 ease-out" style={{ width: `${homePct}%` }} />
                  <div className="h-full rounded-r-full bg-red-500 dark:bg-[#FF1744] transition-all duration-700 ease-out" style={{ width: `${awayPct}%` }} />
                </div>
                <span className="font-monument text-[11px] md:text-[12px] text-red-600 dark:text-[#FF1744] flex-shrink-0">{awayPct}%</span>
              </div>
              {(tournament || (startTimeDisplay && !isLive && !isFinished)) && (
                <div className="flex items-center justify-center gap-1.5 mt-1 flex-wrap">
                  {tournament && (
                    <span className="px-2 py-0.5 rounded-[5px] bg-gray-100 dark:bg-[#1e1e24] text-[10px] font-medium text-gray-500 dark:text-gray-400">
                      {tournament}
                    </span>
                  )}
                  {startTimeDisplay && !isLive && !isFinished && (
                    <span className="px-2 py-0.5 rounded-[5px] bg-gray-100 dark:bg-[#1e1e24] text-[10px] font-medium text-gray-500 dark:text-gray-400">
                      {startTimeDisplay}
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Match info — per-sport stats dropdown (kalshi.com parity). */}
          {perEventLive && (
            <KalshiMatchInfo
              sportType={perEventLive.type}
              homeLabel={homeAbbr}
              awayLabel={awayAbbr}
              competitor1Stats={(perEventLive as any).competitor1Statistics}
              competitor2Stats={(perEventLive as any).competitor2Statistics}
              competitor1IsHome={(perEventLive as any).raw?.live_data?.details?.competitor1_is_home !== false}
              homeStats={(perEventLive as any).homeStats}
              awayStats={(perEventLive as any).awayStats}
              homeSignificantEvents={(perEventLive as any).homeSignificantEvents}
              awaySignificantEvents={(perEventLive as any).awaySignificantEvents}
            />
          )}
        </div>
      </div>

      {/* Chart */}
      {chartMarkets.length >= 2 && (
        <div className="border-t border-gray-100 dark:border-[#1e1e24]">
          <EventMultiOutcomeChart
            markets={chartMarkets}
            eventTitle={eventData.title}
            source="kalshi"
            eventSlug={slug}
            defaultTimeFilter={isLive ? "1H" : "1D"}
          />
        </div>
      )}

    </div>
  );
}

/** Baseball at-bat BSO (balls / strikes / outs) indicator. */
function BSOIndicator({ balls, strikes, outs }: { balls: number; strikes: number; outs: number }) {
  const Row = ({ label, count, max, color }: { label: string; count: number; max: number; color: string }) => (
    <div className="flex items-center gap-1">
      <span className="text-[9px] font-bold text-gray-500 dark:text-gray-400">{label}</span>
      <div className="flex items-center gap-0.5">
        {Array.from({ length: max }).map((_, i) => (
          <span
            key={i}
            className={`w-1.5 h-1.5 rounded-[5px] ${i < count ? color : 'bg-gray-300 dark:bg-gray-700'}`}
          />
        ))}
      </div>
    </div>
  );
  return (
    <div className="flex items-center gap-2.5 px-2 py-1 rounded-[5px] bg-gray-50 dark:bg-[#0f0f12]">
      <Row label="B" count={balls} max={4} color="bg-emerald-500" />
      <Row label="S" count={strikes} max={3} color="bg-red-500" />
      <Row label="O" count={outs} max={3} color="bg-yellow-400" />
    </div>
  );
}

// Legacy in-hero grid kept for reference but no longer mounted — KalshiRelatedMarketsCard
// renders the tabs+rows layout as a separate card below the hero in EventClient.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function RelatedMarketsGrid({ groups }: { groups: RelatedGroup[] }) {
  const { openKalshi } = useTradingModalStore();

  const handleClick = (groupLabel: string, tabLabel: string, m: RelatedMarket) => {
    const yesPrice = m.lastPrice || m.yesBid || 0;
    const altPrice = 1 - yesPrice;
    openKalshi({
      marketTicker: m.ticker,
      marketTitle: `${groupLabel} · ${tabLabel} — ${m.title}`,
      outcome: 'yes',
      currentPrice: yesPrice,
      altPrice,
      imageUrl: m.image,
    });
  };

  return (
    <div className="space-y-3">
      {groups.map((g) => (
        <div key={g.type}>
          <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-1.5">
            {g.label}
          </div>
          <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1">
            {g.tabs.map((tab) => (
              <div
                key={tab.eventTicker}
                className="flex-shrink-0 min-w-[180px] rounded-[5px] border border-gray-200 dark:border-[#1e1e24] bg-gray-50/40 dark:bg-[#0f0f12]/50 p-2.5"
              >
                {g.tabs.length > 1 && (
                  <div className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 text-center mb-1.5">
                    {tab.label}
                  </div>
                )}
                <div className="space-y-1.5">
                  {tab.markets.slice(0, 4).map((m) => {
                    const pct = Math.round((m.lastPrice || m.yesBid || 0) * 100);
                    const cents = Math.round((m.yesAsk || m.lastPrice || m.yesBid || 0) * 100);
                    return (
                      <button
                        key={m.ticker}
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleClick(g.label, tab.label, m);
                        }}
                        className="w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-[5px] bg-white dark:bg-[#1a1a1a] border border-gray-100 dark:border-[#222] hover:border-emerald-500/40 hover:bg-emerald-500/[0.04] transition-colors cursor-pointer"
                        title={m.ticker}
                      >
                        <span className="text-[11px] font-medium text-gray-700 dark:text-gray-300 truncate flex-1 min-w-0 text-left">
                          {m.title}
                        </span>
                        <span className="text-[11px] font-bold tabular-nums text-gray-900 dark:text-white flex-shrink-0">
                          {cents}¢
                        </span>
                        <span className="text-[10px] tabular-nums text-gray-400 dark:text-gray-500 flex-shrink-0">
                          {pct}%
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
